import { useState, useEffect, useCallback } from "react";
import { Router, Wifi, RefreshCw, Power, RotateCcw, Upload, Link2, Unlink, Loader2, CircleDot, Settings2, Save, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const P = {
  model: "InternetGatewayDevice.DeviceInfo.ModelName",
  fw: "InternetGatewayDevice.DeviceInfo.SoftwareVersion",
  ip: "InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ExternalIPAddress",
  ssid: "InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID",
};

const isOnline = (d) => !!d?.last_inform && Date.now() - new Date(d.last_inform).getTime() < 15 * 60 * 1000;
const TASK_LABEL = { setwifi: "Zmiana WiFi", reboot: "Restart", factoryreset: "Reset fabryczny", firmware: "Aktualizacja firmware", refresh: "Odświeżenie parametrów", provision: "Przywrócenie konfiguracji" };
const EVENT_LABEL = { bootstrap: "Reset fabryczny / pierwszy kontakt (BOOTSTRAP)", inform: "Kontakt z ACS (Inform)", task: "Wykonano zadanie" };
const emptyCfg = () => ({ wifi_ssid: "", wifi_pass: "", pppoe_user: "", pppoe_pass: "", extra: [] });

export default function ClientCPE({ clientId, role }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wifi, setWifi] = useState({});
  const [fwUrl, setFwUrl] = useState({});
  const [assignPick, setAssignPick] = useState("");
  const [cfgOpen, setCfgOpen] = useState({});
  const [cfgForm, setCfgForm] = useState({});
  const [histOpen, setHistOpen] = useState({});

  const load = useCallback(() => {
    api.get("/tr069/devices").then((r) => setAll(r.data || [])).catch(() => setAll([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const mine = all.filter((d) => d.client_id === clientId);
  const free = all.filter((d) => !d.client_id);

  const doAssign = async (did) => {
    if (!did) return;
    setBusy(true);
    try { await api.post(`/tr069/devices/${did}/assign`, { client_id: clientId }); toast.success(t("Przypisano router do klienta")); setAssignPick(""); load(); }
    catch { toast.error(t("Błąd przypisania")); } finally { setBusy(false); }
  };
  const doUnassign = async (did) => {
    if (!window.confirm(t("Odpiąć router od klienta?"))) return;
    try { await api.post(`/tr069/devices/${did}/assign`, { client_id: null }); load(); } catch { toast.error(t("Błąd")); }
  };
  const addTask = async (did, type, params) => {
    try {
      await api.post(`/tr069/devices/${did}/tasks`, { type, params: params || {} });
      toast.success(`${t("Zlecono")}: ${t(TASK_LABEL[type] || type)}`);
      setTimeout(load, 500);
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd zlecenia zadania")); }
  };
  const saveWifi = (did) => {
    const w = wifi[did] || {};
    if (!w.ssid) { toast.error(t("Podaj SSID")); return; }
    addTask(did, "setwifi", { ssid: w.ssid, password: w.password || "" });
  };
  const sendFw = (did) => {
    const url = (fwUrl[did] || "").trim();
    if (!url) { toast.error(t("Podaj URL firmware")); return; }
    if (window.confirm(t("Wysłać aktualizację firmware? Router uruchomi się ponownie."))) addTask(did, "firmware", { url });
  };

  const openCfg = (d) => {
    setCfgForm({ ...cfgForm, [d.id]: d.desired_config && Object.keys(d.desired_config).length ? { ...emptyCfg(), ...d.desired_config, extra: d.desired_config.extra || [] } : emptyCfg() });
    setCfgOpen({ ...cfgOpen, [d.id]: !cfgOpen[d.id] });
  };
  const setCf = (did, patch) => setCfgForm({ ...cfgForm, [did]: { ...(cfgForm[did] || emptyCfg()), ...patch } });
  const setExtra = (did, i, patch) => {
    const f = cfgForm[did] || emptyCfg();
    const extra = f.extra.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
    setCf(did, { extra });
  };
  const addExtra = (did) => { const f = cfgForm[did] || emptyCfg(); setCf(did, { extra: [...f.extra, { name: "", value: "" }] }); };
  const delExtra = (did, i) => { const f = cfgForm[did] || emptyCfg(); setCf(did, { extra: f.extra.filter((_, idx) => idx !== i) }); };
  const saveCfg = async (did) => {
    try { await api.post(`/tr069/devices/${did}/config`, cfgForm[did] || emptyCfg()); toast.success(t("Zapisano wzorzec konfiguracji")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd zapisu wzorca")); }
  };
  const provisionNow = async (did) => {
    try { await api.post(`/tr069/devices/${did}/provision`); toast.success(t("Zlecono przywrócenie konfiguracji")); setTimeout(load, 500); }
    catch (e) { toast.error(e.response?.data?.detail || t("Najpierw zapisz wzorzec konfiguracji")); }
  };

  return (
    <div className="p-4 border-b border-slate-800" data-testid="client-cpe-block">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1.5"><Router size={12} /> {t("Router klienta (TR-069)")} ({mine.length})</div>
        <button onClick={load} data-testid="cpe-refresh-list" title={t("Odśwież listę")} className="p-1 rounded hover:bg-slate-700 text-slate-400"><RefreshCw size={13} /></button>
      </div>

      {canEdit && (
        <div className="flex gap-2 mb-3" data-testid="cpe-assign-row">
          <select value={assignPick} onChange={(e) => setAssignPick(e.target.value)} data-testid="cpe-assign-select"
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
            <option value="">{free.length ? t("Wybierz wykryty router…") : t("Brak wolnych routerów (czekają na pierwszy kontakt)")}</option>
            {free.map((d) => (
              <option key={d.id} value={d.id}>{((d.params || {})[P.model] || d.model || d.product_class || "CPE")} · {d.serial || d.id}{isOnline(d) ? " ●" : ""}</option>
            ))}
          </select>
          <button onClick={() => doAssign(assignPick)} disabled={!assignPick || busy} data-testid="cpe-assign-btn"
            className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 rounded px-3 py-1.5 text-xs flex items-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} {t("Przypisz")}
          </button>
        </div>
      )}

      {mine.length === 0 ? (
        <div className="text-xs text-slate-500">{t("Brak przypisanego routera. Routery pojawiają się na liście po pierwszym połączeniu z ACS.")}</div>
      ) : (
        <div className="space-y-3" data-testid="cpe-device-list">
          {mine.map((d) => {
            const p = d.params || {};
            const online = isOnline(d);
            const tasks = (d.tasks || []).slice().reverse();
            const w = wifi[d.id] || {};
            const hasCfg = d.desired_config && (d.desired_config.wifi_ssid || d.desired_config.pppoe_user || (d.desired_config.extra || []).length);
            const cf = cfgForm[d.id] || emptyCfg();
            return (
              <div key={d.id} data-testid={`cpe-device-${d.id}`} className="rounded-xl border border-cyan-500/15 bg-slate-900/60 backdrop-blur-md p-2.5 space-y-2 hover:border-cyan-500/30 transition-colors shadow-lg shadow-black/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <CircleDot size={12} className={online ? "text-green-400" : "text-slate-600"} />
                    <span className="text-sm font-semibold text-slate-200 truncate">{p[P.model] || d.model || d.product_class || "CPE"}</span>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${online ? "bg-green-900/50 text-green-300" : "bg-slate-700 text-slate-400"}`} data-testid={`cpe-status-${d.id}`}>
                    {online ? t("online") : t("offline")}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  <div className="text-slate-500">S/N</div><div className="text-slate-300 font-mono truncate">{d.serial || "—"}</div>
                  <div className="text-slate-500">IP WAN</div><div className="text-slate-300 font-mono truncate" data-testid={`cpe-ip-${d.id}`}>{p[P.ip] || "—"}</div>
                  <div className="text-slate-500">WiFi SSID</div><div className="text-slate-300 truncate" data-testid={`cpe-ssid-${d.id}`}>{p[P.ssid] || "—"}</div>
                  <div className="text-slate-500">Firmware</div><div className="text-slate-300 truncate" data-testid={`cpe-fw-${d.id}`}>{p[P.fw] || "—"}</div>
                  <div className="text-slate-500">{t("Ost. kontakt")}</div><div className="text-slate-400 truncate">{d.last_inform ? new Date(d.last_inform).toLocaleString("pl-PL") : "—"}</div>
                </div>

                {canEdit && (
                  <>
                    <div className="pt-1.5 border-t border-slate-800">
                      <div className="text-[10px] text-slate-500 uppercase mb-1 flex items-center gap-1"><Wifi size={11} /> {t("Zmień WiFi")}</div>
                      <div className="flex gap-1.5">
                        <input value={w.ssid ?? ""} onChange={(e) => setWifi({ ...wifi, [d.id]: { ...w, ssid: e.target.value } })}
                          placeholder="SSID" data-testid={`cpe-ssid-input-${d.id}`}
                          className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                        <input value={w.password ?? ""} onChange={(e) => setWifi({ ...wifi, [d.id]: { ...w, password: e.target.value } })}
                          placeholder={t("hasło")} data-testid={`cpe-wifi-pass-${d.id}`}
                          className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                        <button onClick={() => saveWifi(d.id)} data-testid={`cpe-wifi-save-${d.id}`}
                          className="bg-cyan-700 hover:bg-cyan-600 rounded px-2.5 text-xs shrink-0">{t("Ustaw")}</button>
                      </div>
                    </div>

                    <div className="flex gap-1.5">
                      <input value={fwUrl[d.id] ?? ""} onChange={(e) => setFwUrl({ ...fwUrl, [d.id]: e.target.value })}
                        placeholder={t("URL firmware (.bin)")} data-testid={`cpe-fw-input-${d.id}`}
                        className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                      <button onClick={() => sendFw(d.id)} data-testid={`cpe-fw-btn-${d.id}`}
                        className="bg-amber-700 hover:bg-amber-600 rounded px-2.5 text-xs flex items-center gap-1 shrink-0"><Upload size={12} /> {t("Wyślij")}</button>
                    </div>

                    <div className="grid grid-cols-4 gap-1.5">
                      <button onClick={() => addTask(d.id, "refresh")} data-testid={`cpe-refresh-${d.id}`}
                        className="bg-slate-700 hover:bg-slate-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><RefreshCw size={12} /> {t("Odśwież")}</button>
                      <button onClick={() => addTask(d.id, "reboot")} data-testid={`cpe-reboot-${d.id}`}
                        className="bg-slate-700 hover:bg-slate-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><Power size={12} /> {t("Restart")}</button>
                      <button onClick={() => { if (window.confirm(t("Przywrócić ustawienia fabryczne routera?"))) addTask(d.id, "factoryreset"); }} data-testid={`cpe-reset-${d.id}`}
                        className="bg-red-800 hover:bg-red-700 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><RotateCcw size={12} /> {t("Reset")}</button>
                      <button onClick={() => doUnassign(d.id)} data-testid={`cpe-unassign-${d.id}`}
                        className="bg-slate-700 hover:bg-slate-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><Unlink size={12} /> {t("Odepnij")}</button>
                    </div>

                    {/* Wzorzec konfiguracji (auto-provisioning po resecie) */}
                    <div className="pt-1.5 border-t border-slate-800">
                      <button onClick={() => openCfg(d)} data-testid={`cpe-cfg-toggle-${d.id}`}
                        className="w-full flex items-center justify-between text-[10px] text-slate-400 uppercase hover:text-slate-200">
                        <span className="flex items-center gap-1"><Settings2 size={11} /> {t("Wzorzec konfiguracji (auto po resecie)")}{hasCfg ? " ✓" : ""}</span>
                        <span>{cfgOpen[d.id] ? "▾" : "▸"}</span>
                      </button>
                      {cfgOpen[d.id] && (
                        <div className="mt-2 space-y-1.5" data-testid={`cpe-cfg-form-${d.id}`}>
                          <div className="grid grid-cols-2 gap-1.5">
                            <input value={cf.wifi_ssid} onChange={(e) => setCf(d.id, { wifi_ssid: e.target.value })} placeholder="WiFi SSID" data-testid={`cpe-cfg-ssid-${d.id}`}
                              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                            <input value={cf.wifi_pass} onChange={(e) => setCf(d.id, { wifi_pass: e.target.value })} placeholder={t("WiFi hasło")} data-testid={`cpe-cfg-wifipass-${d.id}`}
                              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                            <input value={cf.pppoe_user} onChange={(e) => setCf(d.id, { pppoe_user: e.target.value })} placeholder="PPPoE login" data-testid={`cpe-cfg-ppuser-${d.id}`}
                              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                            <input value={cf.pppoe_pass} onChange={(e) => setCf(d.id, { pppoe_pass: e.target.value })} placeholder="PPPoE hasło" data-testid={`cpe-cfg-pppass-${d.id}`}
                              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
                          </div>
                          {cf.extra.map((e, i) => (
                            <div key={i} className="flex gap-1.5">
                              <input value={e.name} onChange={(ev) => setExtra(d.id, i, { name: ev.target.value })} placeholder={t("Parametr TR-069 (Name)")} data-testid={`cpe-cfg-extra-name-${d.id}-${i}`}
                                className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono text-slate-200" />
                              <input value={e.value} onChange={(ev) => setExtra(d.id, i, { value: ev.target.value })} placeholder={t("Wartość")} data-testid={`cpe-cfg-extra-val-${d.id}-${i}`}
                                className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] text-slate-200" />
                              <button onClick={() => delExtra(d.id, i)} className="p-1 rounded hover:bg-red-600/30 text-red-400 shrink-0"><Trash2 size={12} /></button>
                            </div>
                          ))}
                          <button onClick={() => addExtra(d.id)} data-testid={`cpe-cfg-add-extra-${d.id}`} className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"><Plus size={11} /> {t("Dodaj parametr (DNS / port / hasło admina)")}</button>
                          <div className="flex gap-1.5 pt-1">
                            <button onClick={() => saveCfg(d.id)} data-testid={`cpe-cfg-save-${d.id}`} className="flex-1 bg-cyan-700 hover:bg-cyan-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><Save size={12} /> {t("Zapisz wzorzec")}</button>
                            <button onClick={() => provisionNow(d.id)} data-testid={`cpe-provision-${d.id}`} className="flex-1 bg-indigo-700 hover:bg-indigo-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><Wand2 size={12} /> {t("Provision teraz")}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {tasks.length > 0 && (
                  <div className="pt-1.5 border-t border-slate-800">
                    <div className="text-[10px] text-slate-500 uppercase mb-1">{t("Kolejka zadań ACS")}</div>
                    <div className="space-y-1" data-testid={`cpe-tasks-${d.id}`}>
                      {tasks.slice(0, 6).map((tk) => (
                        <div key={tk.tid} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-300">{t(TASK_LABEL[tk.type] || tk.type)}{tk.auto ? ` (${t("auto")})` : ""}</span>
                          <span className={`px-1.5 rounded ${tk.status === "done" ? "text-green-300" : tk.status === "sent" ? "text-amber-300" : "text-slate-400"}`}>
                            {tk.status === "done" ? t("wykonane") : tk.status === "sent" ? t("wysłane") : t("oczekuje")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Historia zdarzeń CPE */}
                {(d.events_log || []).length > 0 && (
                  <div className="pt-1.5 border-t border-slate-800">
                    <button onClick={() => setHistOpen({ ...histOpen, [d.id]: !histOpen[d.id] })} data-testid={`cpe-hist-toggle-${d.id}`}
                      className="w-full flex items-center justify-between text-[10px] text-slate-500 uppercase hover:text-slate-300">
                      <span>{t("Historia zdarzeń")} ({(d.events_log || []).length})</span>
                      <span>{histOpen[d.id] ? "▾" : "▸"}</span>
                    </button>
                    {histOpen[d.id] && (
                      <div className="mt-1.5 space-y-1 max-h-48 overflow-auto" data-testid={`cpe-hist-${d.id}`}>
                        {(d.events_log || []).slice().reverse().map((ev, i) => (
                          <div key={i} className="flex items-start justify-between text-[11px] gap-2">
                            <span className={`${ev.kind === "bootstrap" ? "text-red-300" : "text-slate-300"}`}>
                              {t(EVENT_LABEL[ev.kind] || ev.kind)}
                              {ev.kind === "task" && ev.task_type ? `: ${t(TASK_LABEL[ev.task_type] || ev.task_type)}` : ""}
                            </span>
                            <span className="text-slate-500 shrink-0 whitespace-nowrap">{ev.ts ? new Date(ev.ts).toLocaleString("pl-PL") : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
